/**
 * AI Usage — per-org token / cost / command rollup.
 *
 * Three sections:
 *   1. Three global charts (cost/day, commands/day, cost-per-command/day)
 *   2. High-spend alert banner if any org > $50/month estimated cost
 *   3. Per-org table — sorted by spend (highest first), each row shows
 *      command counts (today / week / month), credits, cost, top
 *      commands, tier mix.
 *
 * Single ``GET /admin/ai-usage`` call hydrates everything.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AiUsageResponse, getAiUsage } from "@/api/admin";
import { cn, fmtNum, fmtUsd } from "@/lib/utils";

export function AiUsagePage() {
  const [data, setData] = useState<AiUsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAiUsage()
      .then((d) => !cancelled && setData(d))
      .catch((e) =>
        !cancelled && setError(e?.message || "Failed to load AI usage"),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  const highSpenders = useMemo(
    () => data?.per_org.filter((r) => r.alert_high_spend) || [],
    [data],
  );

  if (error) {
    return <div className="p-8 text-sm text-danger">Failed: {error}</div>;
  }
  if (!data) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">AI Usage</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Command bar volume, credits, and estimated Anthropic cost
          per org. Last 30 days.
        </p>
      </header>

      {/* High-spend alert. Only renders when at least one org is
          burning > $50/month — keeps the surface uncluttered when
          everyone is in a healthy band. */}
      {highSpenders.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-slate-900">
              {highSpenders.length}{" "}
              {highSpenders.length === 1 ? "org" : "orgs"} burning &gt; $50
              in API costs this month
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              {highSpenders
                .map((o) => `${o.name} (${fmtUsd(o.estimated_cost_usd)})`)
                .join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* ── Global trend charts ────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Daily API cost (USD)">
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data.daily_cost}>
              <defs>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                fontSize={11}
                stroke="#94A3B8"
              />
              <YAxis fontSize={11} stroke="#94A3B8" />
              <Tooltip formatter={(v: number) => fmtUsd(v)} />
              <Area
                type="monotone"
                dataKey="cost_usd"
                stroke="#EF4444"
                strokeWidth={2}
                fill="url(#costGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Commands per day">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.daily_cost}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                fontSize={11}
                stroke="#94A3B8"
              />
              <YAxis fontSize={11} stroke="#94A3B8" allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="commands"
                stroke="#6366F1"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost per command (USD)">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.daily_cost}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => d.slice(5)}
                fontSize={11}
                stroke="#94A3B8"
              />
              <YAxis fontSize={11} stroke="#94A3B8" />
              <Tooltip
                formatter={(v: number) => `$${v.toFixed(4)}`}
              />
              <Line
                type="monotone"
                dataKey="cost_per_command_usd"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      {/* ── Per-org table ───────────────────────────────── */}
      <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <header className="px-4 py-2.5 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">
            Usage by customer ({data.per_org.length})
          </h2>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Org</th>
                <th className="text-right px-4 py-2 font-semibold">
                  Today / Week / Month
                </th>
                <th className="text-right px-4 py-2 font-semibold">
                  Credits used
                </th>
                <th className="text-right px-4 py-2 font-semibold">Est. cost</th>
                <th className="text-left px-4 py-2 font-semibold">
                  Top commands
                </th>
                <th className="text-left px-4 py-2 font-semibold">Tier mix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.per_org.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    No customer activity yet.
                  </td>
                </tr>
              )}
              {data.per_org.map((row) => (
                <tr
                  key={row.org_id}
                  className={cn(
                    "hover:bg-slate-50",
                    row.alert_high_spend && "bg-warning/5",
                  )}
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/customers/${row.org_id}`}
                      className="font-medium text-slate-900 hover:text-accent"
                    >
                      {row.name}
                    </Link>
                    <div className="text-[11px] text-slate-500 capitalize">
                      {row.plan}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-sm">
                    <span className="text-slate-900">
                      {fmtNum(row.commands_today)}
                    </span>{" "}
                    <span className="text-slate-400">
                      / {fmtNum(row.commands_week)}
                    </span>{" "}
                    <span className="text-slate-400">
                      / {fmtNum(row.commands_month)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {fmtNum(row.credits_consumed)}
                  </td>
                  <td
                    className={cn(
                      "px-4 py-2.5 text-right tabular-nums font-medium",
                      row.alert_high_spend
                        ? "text-warning"
                        : "text-slate-900",
                    )}
                  >
                    {fmtUsd(row.estimated_cost_usd)}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {row.top_commands.length === 0 ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {row.top_commands.map((t) => (
                          <li key={t.intent} className="text-slate-600">
                            <span className="font-mono">{t.intent}</span>
                            <span className="text-slate-400 ml-1">
                              ×{t.count}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <TierBar tiers={row.tier_breakdown} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

function TierBar({
  tiers,
}: {
  tiers: { haiku: number; sonnet: number; skills: number };
}) {
  const total = tiers.haiku + tiers.sonnet + tiers.skills;
  if (total === 0)
    return <span className="text-xs text-slate-400">no calls</span>;
  const pct = (n: number) => Math.round((n / total) * 100);
  return (
    <div className="min-w-[160px]">
      <div className="flex h-2 rounded overflow-hidden bg-slate-100">
        <div
          className="bg-success"
          style={{ width: `${pct(tiers.haiku)}%` }}
          title={`Haiku ${tiers.haiku}`}
        />
        <div
          className="bg-accent"
          style={{ width: `${pct(tiers.sonnet)}%` }}
          title={`Sonnet ${tiers.sonnet}`}
        />
        <div
          className="bg-warning"
          style={{ width: `${pct(tiers.skills)}%` }}
          title={`Skills ${tiers.skills}`}
        />
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
        {pct(tiers.haiku)}% H · {pct(tiers.sonnet)}% S · {pct(tiers.skills)}% Sk
      </div>
    </div>
  );
}
